import { Tooltip as AntTooltip } from 'antd';
import useWindowSize from 'app/hooks/useWindowSize';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { formatNumber, renderReplaceText } from 'utils/helper';

export const REPORT_MOBILE_WIDTH = 768;

export const useIsReportMobile = (): boolean => {
  const { windowSize } = useWindowSize();
  return windowSize.width <= REPORT_MOBILE_WIDTH;
};

export const ReportCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  .report-card-title {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .report-card-note {
    font-style: italic;
    font-size: 14px;
    color: var(--color-12);
  }
  .report-card-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
  }
  @media (max-width: ${REPORT_MOBILE_WIDTH}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    .sub-title {
      font-size: 16px;
    }
    .report-card-actions {
      width: 100%;
    }
  }
`;

const MIN_SLOT_WIDTH = 64;

export const ChartScroll = styled.div<{ $itemCount: number }>`
  @media (max-width: ${REPORT_MOBILE_WIDTH}px) {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    .chart-scroll-inner {
      min-width: ${({ $itemCount }) => $itemCount * MIN_SLOT_WIDTH}px;
    }
  }
`;

export const formatCurrencyShort = (
  value: number,
  fractionDigits = 1,
): string => {
  const units = [
    { threshold: 1000000000000000, suffix: 'Q' },
    { threshold: 1000000000000, suffix: 'T' },
    { threshold: 1000000000, suffix: 'B' },
    { threshold: 1000000, suffix: 'M' },
    { threshold: 1000, suffix: 'K' },
  ];
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  for (const unit of units) {
    if (absValue >= unit.threshold) {
      const factor = Math.pow(10, fractionDigits);
      const rounded = Math.round((absValue / unit.threshold) * factor) / factor;
      return `${sign}${rounded}${unit.suffix}`;
    }
  }
  return `${sign}${absValue}`;
};

export const calcRevenuePercent = (
  revenue: number,
  totalRevenue: number,
): string =>
  totalRevenue ? String(Math.round((revenue / totalRevenue) * 1000) / 10) : '0';

const TICK_CHAR_WIDTH = 5.6;
const MIN_TICK_LENGTH = 3;
const DEFAULT_TICK_LENGTH = 8;
const TICK_GAP = 12;

export const AxisTick = (props: any): ReactElement<SVGElement> => {
  const {
    x = 0,
    y = 0,
    payload,
    visibleTicksCount = 1,
    width = 0,
    fullNameByShortName = {},
  } = props;
  const shortName: string = payload?.value ?? '';
  const fullName = fullNameByShortName[shortName] ?? shortName;

  const slotWidth = width / Math.max(visibleTicksCount, 1) - TICK_GAP;
  const maxLength =
    slotWidth > 0
      ? Math.max(MIN_TICK_LENGTH, Math.floor(slotWidth / TICK_CHAR_WIDTH))
      : DEFAULT_TICK_LENGTH;
  const isTruncated = shortName.length > maxLength;
  const displayName = isTruncated
    ? `${shortName.slice(0, maxLength)}...`
    : shortName;

  const tickText = (
    <text
      dy={12}
      textAnchor="middle"
      fontSize={10}
      fill="#666"
      style={{ cursor: 'default' }}
    >
      {displayName}
    </text>
  );

  return (
    <g transform={`translate(${x},${y})`}>
      {displayName === fullName ? (
        tickText
      ) : (
        <AntTooltip title={fullName}>{tickText}</AntTooltip>
      )}
    </g>
  );
};

interface IReportTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  translations?: any;
  totalRevenue?: number;
}

export const ReportTooltip = ({
  active,
  payload,
  label,
  translations,
  totalRevenue,
}: IReportTooltipProps) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload ?? {};
  const percent = totalRevenue
    ? calcRevenuePercent(item?.revenue ?? 0, totalRevenue)
    : null;
  return (
    <TooltipWrapper>
      <div className="tooltip-title">
        {item?.fullName ?? item?.name ?? label}
      </div>
      <StyledRow>
        {percent !== null
          ? renderReplaceText(translations?.['b2b.txt.revenueWithPercentage'], {
              value: formatNumber(item?.revenue ?? 0, '.'),
              percent: percent,
            })
          : renderReplaceText(translations?.['b2b.txt.revenueValue'], {
              value: formatNumber(item?.revenue ?? 0, '.'),
            })}
      </StyledRow>
      <StyledRow>
        {renderReplaceText(translations?.['b2b.txt.ticketQuantityReport'], {
          value: formatNumber(item?.ticketCount ?? 0, '.'),
        })}
      </StyledRow>
    </TooltipWrapper>
  );
};

export const TooltipWrapper = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 12px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
  .tooltip-title {
    font-weight: 600;
    margin-bottom: 8px;
  }
  .text-ticket {
    color: var(--color-12);
  }
  .text-number-ticket {
    font-weight: 600;
  }
`;

export const StyledRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const ReportCardTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  && .title,
  && .sub-title {
    margin-bottom: 0;
  }
  .report-card-note {
    font-style: italic;
    font-size: 14px;
    color: var(--color-12);
  }
`;
